import React, { useState } from 'react';
import useApi from '../../hooks/useApi';
import toast from 'react-hot-toast';
import { X, Loader2, AlertCircle, Users, Clock, Repeat, Wifi, RefreshCw } from 'lucide-react';

// ── Small helpers ────────────────────────────────────────────
const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-GH', { day: 'numeric', month: 'short' }) : '—';

const fmtDuration = (seconds) => {
  if (!seconds) return '0 min';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

// Same rough thresholds Daily's own dashboard implies: clean under ~2%,
// noticeable up to ~10%, bad above that.
const packetLossTone = (pct) => {
  if (pct === null || pct === undefined) return 'text-forest-300';
  if (pct <= 2) return 'text-green-600';
  if (pct <= 10) return 'text-amber-600';
  return 'text-rust-500';
};

const PacketLossCell = ({ avgPct, worstPct }) => {
  if (avgPct === null || avgPct === undefined) {
    return <span className="text-[11px] text-forest-300">—</span>;
  }
  return (
    <div className="flex flex-col">
      <span className={`text-xs font-semibold ${packetLossTone(avgPct)}`}>{avgPct}%</span>
      {worstPct !== null && worstPct !== undefined && worstPct > avgPct && (
        <span className="text-[10px] text-forest-400">worst {worstPct}%</span>
      )}
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value }) => (
  <div className="bg-cream-50 border border-cream-200 rounded-xl px-4 py-3 flex-1 min-w-[120px]">
    <div className="flex items-center gap-1.5 text-forest-400 mb-1">
      <Icon size={12} />
      <span className="text-[10px] font-semibold uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-xl font-serif font-semibold text-forest-900">{value}</p>
  </div>
);

/**
 * AttendanceModal
 * props:
 *  - title: string shown in the header (e.g. seminar title, session label)
 *  - roomLabel: optional secondary line (e.g. room name / date)
 *  - fetchFn: () => Promise — one of attendanceService.getForSession / getForGroupSession / getForSeminar
 *  - syncFn: () => Promise — matching syncSession / syncGroupSession / syncSeminar, pulls fresh data from Daily on demand
 *  - onClose: () => void
 */
const AttendanceModal = ({ title, roomLabel, fetchFn, syncFn, onClose }) => {
  const { data, loading, error, refetch } = useApi(fetchFn, []);
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    if (!syncFn) return;
    setSyncing(true);
    try {
      await syncFn();
      toast.success('Attendance synced from Daily');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not sync attendance from Daily');
    } finally {
      setSyncing(false);
    }
  };

  const summary   = data?.summary   ?? { totalJoins: 0, uniqueAttendees: 0, totalParticipantMinutes: 0 };
  const attendees = data?.attendees ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-cream-100 flex-shrink-0">
          <div>
            <h3 className="font-serif text-lg font-semibold text-forest-900">Attendance</h3>
            <p className="text-sm text-forest-600 mt-0.5">{title}</p>
            {roomLabel && <p className="text-xs text-forest-400 mt-0.5">{roomLabel}</p>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {syncFn && (
              <button onClick={handleSync} disabled={syncing}
                className="flex items-center gap-1.5 text-xs font-medium border border-cream-200 text-forest-600 hover:border-forest-400 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50">
                <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Syncing...' : 'Sync Now'}
              </button>
            )}
            <button onClick={onClose} className="text-forest-400 hover:text-forest-700">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-6 space-y-5">

          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-forest-600" />
            </div>
          )}

          {!loading && error && (
            <div className="flex items-center gap-2 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-4 py-3 text-sm">
              <AlertCircle size={15} /> Could not load attendance data.
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Summary cards */}
              <div className="flex gap-3 flex-wrap">
                <SummaryCard icon={Repeat} label="Total Joins" value={summary.totalJoins} />
                <SummaryCard icon={Users}  label="Unique Attendees" value={summary.uniqueAttendees} />
                <SummaryCard icon={Clock}  label="Participant-Minutes" value={summary.totalParticipantMinutes} />
              </div>

              {attendees.length === 0 ? (
                <div className="text-center py-16 text-forest-400">
                  <Users size={32} className="mx-auto mb-3 opacity-30" />
                  <p className="font-serif text-lg text-forest-600">No attendance data yet</p>
                  <p className="text-sm mt-1">
                    This fills in automatically a few seconds after the call ends —
                    check back shortly if the session just wrapped up.
                  </p>
                  {syncFn && (
                    <button onClick={handleSync} disabled={syncing}
                      className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium border border-forest-200 text-forest-600 hover:bg-forest-50 px-3.5 py-2 rounded-full transition-colors disabled:opacity-50">
                      <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
                      {syncing ? 'Syncing...' : 'Pull from Daily now'}
                    </button>
                  )}
                </div>
              ) : (
                <div className="border border-cream-200 rounded-2xl overflow-hidden">
                  {/* Table header */}
                  <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-cream-50 border-b border-cream-100 text-[10px] font-semibold text-forest-500 uppercase tracking-widest">
                    <div className="col-span-3">Name</div>
                    <div className="col-span-2">Date</div>
                    <div className="col-span-2">Joined</div>
                    <div className="col-span-2">Left</div>
                    <div className="col-span-1">Duration</div>
                    <div className="col-span-1 flex items-center gap-1"><Wifi size={10} /> Send</div>
                    <div className="col-span-1">Recv</div>
                  </div>

                  <div className="divide-y divide-cream-50 max-h-80 overflow-y-auto">
                    {attendees.map((a) => (
                      <div key={a.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center">
                        <div className="col-span-3 min-w-0">
                          <p className="text-sm text-forest-800 truncate">{a.name}</p>
                          {a.user?.email && <p className="text-[10px] text-forest-400 truncate">{a.user.email}</p>}
                        </div>
                        <div className="col-span-2 text-xs text-forest-600">{fmtDate(a.joinedAt)}</div>
                        <div className="col-span-2 text-xs text-forest-600">{fmtTime(a.joinedAt)}</div>
                        <div className="col-span-2 text-xs text-forest-600">{a.leftAt ? fmtTime(a.leftAt) : '—'}</div>
                        <div className="col-span-1 text-xs text-forest-700">{fmtDuration(a.durationSeconds)}</div>
                        <div className="col-span-1">
                          <PacketLossCell avgPct={a.callQuality?.avgPacketLossSendPct} worstPct={a.callQuality?.worstPacketLossSendPct} />
                        </div>
                        <div className="col-span-1">
                          <PacketLossCell avgPct={a.callQuality?.avgPacketLossRecvPct} worstPct={a.callQuality?.worstPacketLossRecvPct} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceModal;